import { OmcRegistration } from '@modules/applications/omc-applications/responses/omc-applications.response';
import { PageableParamInput } from '@modules/external-services/company-management/requests/pageable-param.search';
import { Page } from '@modules/external-services/company-management/response/company-staffs.response';
import {
  ActiveOpenDemandCycle,
  OmcDemandCycleDetail,
  OmcDemandCycleListItem,
} from '@modules/external-services/omc-management/response/omc-demand-cycles.response';
import { OmcContract } from '@modules/registration/omc-registration/responses/fetch-contract.response';
import { createApiActions } from '@shared/ngrx-factory/factory.actions';
import { SaveOmcDemandInputInput } from '../requests/save-draft-demand.request';
import { SaveDemandProjectionInputInput } from '../requests/save-omc-demand-projects.request';
import { SaveTransitConsigneeInputInput } from '../requests/save-transit-consigne.request';
import { SaveTransitStockInputInput } from '../requests/save-transit-stock.request';
import {
  OmcTerminalAgreementReqInput,
  OmcTerminalSameTinReqInput,
} from '../requests/omc-terminal-agreement.request';
import { OmcTerminalAgreementUpdateReqInput } from '../requests/update-terminal-agreement.request';
import {
  CurrentOmcDemandDraftResponse,
  DeleteOmcDemandDraftPayload,
  OmcDemandSubmissionDetail,
  OmcDemandSubmissionsResponse,
  OverduePendingBillsSummaryPayload,
  RequiredGuaranteeSummaryPayload,
  GetLoggedInOmcLastMonthDemandProjectionPayload,
  RequiredOmcProductMarketShareSummaryPayload,
} from '../response/omc-demand-submissions.response';
import {
  CheckOmcTerminalSameTinPayload,
  CreateOmcTerminalAgreementPayload,
  OmcTerminalAgreement,
  OmcTerminalAgreementDetails,
  SubmitOmcTerminalAgreementPayload,
  UpdateOmcTerminalAgreementPayload,
} from '../response/omc-terminal-agreement.response';
import {
  CreateOmcStockReportPayload,
  OmcStockReport,
  SubmitOmcStockReportPayload,
  SubmitOmcUsageReportPayload,
  UpdateOmcUsageReportPayload,
  UpdateOmcStockReportPayload,
} from '../response/omc-stock-report.response';
import {
  SaveDemandProjectionPayload,
  SaveDraftDemandPayload,
} from '../response/save-draft-demand.response';
import { SaveTransitConsigneePayload } from '../response/save-transit-consignee.response';
import { SaveTransitStockPayload } from '../response/save-transit-stock.response';
import { DeleteTransitStockPayload } from '../response/delete-transit-stock.response';
import { DeleteTransitConsigneePayload } from '../response/delete-transit-consignee.response';
import {
  DeleteOmcDemandCargoProductInput,
  DeleteOmcDemandCargoProductPayload,
  DeleteOmcDemandProductUsageInput,
  DeleteOmcDemandProductUsagePayload,
} from '../response/delete-omc-demand-cargo-product.response';
import { SubmitOmcDemandPayload } from '../response/submit-omc-demand.response';
import { OmcStockReportReqInput } from '../requests/omc-daily-stock-report.request';
import {
  OmcProductUsageReqInput,
  OmcUsageUpdateReqInput,
} from '../requests/omc-usage-report.request';
import { OmcStockReportUpdateReqInput } from '../requests/update-somc-stock-report.request';
import { OmcContractSignReqInput } from '../requests/omc-sign-contract.request';
import { OmcCertificatePreview } from '../response/omc-certificate-preview.response';
import {
  SignOmcContractResponse,
  SignOmcContractResponsePayload,
} from '../response/sing-omc-contract.response';

export interface LoadOmcListResponse {
  data: Page<OmcRegistration>;
}

export interface LoadOmcContractsResponse {
  data: Page<OmcContract>;
}

export interface LoadOmcDemandSubmissionsResponse {
  data: Page<any>;
}

export interface LoadOmcDemandCyclesResponse {
  data: Page<OmcDemandCycleListItem>;
}

export interface LoadActiveOpenDemandCyclesResponse {
  data: ActiveOpenDemandCycle[];
}

export interface LoadOmcDemandCycleByUuidResponse {
  data: OmcDemandCycleDetail | null;
}

export interface LoadOmcDemandByUuidResponse {
  data: OmcDemandSubmissionDetail | null;
}

export interface LoadCurrentOmcDemandDraftResponse {
  data: CurrentOmcDemandDraftResponse['currentOmcDemandDraft']['data'];
}

export interface LoadOmcDemandByUuidRequest {
  uuid: string;
  includeCycle?: boolean;
  includeLines?: boolean;
  includeTerminals?: boolean;
  includeConsignees?: boolean;
  includeTransitStocks?: boolean;
  includeProjections?: boolean;
  includeAttachments?: boolean;
}

export interface LoadOmcTerminalAgreementsResponse {
  data: Page<OmcTerminalAgreement>;
}

export interface LoadOmcTerminalAgreementByUuidResponse {
  data: OmcTerminalAgreementDetails | null;
}

export interface LoadOmcStockReportsResponse {
  data: OmcStockReport[];
}

export interface LoadOmcStockReportDetailResponse {
  data: OmcStockReport | null;
}

export interface LoadOmcCertificatePreviewResponse {
  data: OmcCertificatePreview | null;
}

export const omcListActions = createApiActions<PageableParamInput, LoadOmcListResponse>(
  'OMC Management',
  'Load OMC List',
);

export const omcContractsListActions = createApiActions<
  PageableParamInput,
  LoadOmcContractsResponse
>('OMC Management', 'Load OMC Contracts List');

export const omcDemandProjectionActions = createApiActions<
  SaveDemandProjectionInputInput,
  SaveDemandProjectionPayload
>('OMC Management', 'Save OMC Demand Projection');

export const omcTransitConsigneesActions = createApiActions<
  SaveTransitConsigneeInputInput,
  SaveTransitConsigneePayload
>('OMC Management', 'Save Transit Consignees');

export const omcTransitStocksActions = createApiActions<
  SaveTransitStockInputInput,
  SaveTransitStockPayload
>('OMC Management', 'Save Transit Stocks');

export const omcDemandSubmissionsActions = createApiActions<
  PageableParamInput,
  { data: OmcDemandSubmissionsResponse['omcDemandSubmissions'] }
>('OMC Management', 'Load OMC Demand Submissions');

export const omcDemandCyclesActions = createApiActions<
  PageableParamInput,
  LoadOmcDemandCyclesResponse
>('OMC Management', 'Load OMC Demand Windows');

export const omcActiveOpenDemandCyclesActions = createApiActions<
  null,
  LoadActiveOpenDemandCyclesResponse
>('OMC Management', 'Load Active Open Demand Windows');

export const omcDemandCycleByUuidActions = createApiActions<
  string,
  LoadOmcDemandCycleByUuidResponse
>('OMC Management', 'Load OMC Demand Window By Uuid');

export const omcDemandByUuidActions = createApiActions<
  LoadOmcDemandByUuidRequest,
  LoadOmcDemandByUuidResponse
>(
  'OMC Management',
  'Load OMC Demand By Uuid',
);

export const omcCurrentWindowDemandDraftActions = createApiActions<
  null,
  LoadCurrentOmcDemandDraftResponse
>('OMC Management', 'Load Current Window Demand Draft');

export const omcDraftDemandActions = createApiActions<
  SaveOmcDemandInputInput,
  SaveDraftDemandPayload
>('OMC Management', 'Save OMC Draft Demand');

export const omcSubmitDemandActions = createApiActions<
  { uuid: string, isDeclared: boolean }
  , SubmitOmcDemandPayload>(
    'OMC Management',
    'Submit OMC Demand',
  );

export const omcDeleteDemandDraftActions = createApiActions<string, DeleteOmcDemandDraftPayload>(
  'OMC Management',
  'Delete OMC Demand Draft',
);

export const omcRequiredGuaranteeSummaryActions = createApiActions<
  string,
  RequiredGuaranteeSummaryPayload
>('OMC Management', 'Load Required Guarantee Summary');

export const omcProductMarketShareSummaryActions = createApiActions<
  string,
  RequiredOmcProductMarketShareSummaryPayload
>('OMC Management', 'Load Required OMC Product Market Share Summary');

export const omcOverduePendingBillsSummaryActions = createApiActions<
  null,
  OverduePendingBillsSummaryPayload
>('OMC Management', 'Load Overdue Pending Bills Summary');

export const omcTerminalAgreementsActions = createApiActions<
  { pageable: PageableParamInput; omcUuid: string },
  LoadOmcTerminalAgreementsResponse
>('OMC Management', 'Load OMC Terminal Agreements');

export const omcTerminalAgreementByUuidActions = createApiActions<
  string,
  LoadOmcTerminalAgreementByUuidResponse
>('OMC Management', 'Load OMC Terminal Agreement By Uuid');

export const omcStockReportsActions = createApiActions<string | null, LoadOmcStockReportsResponse>(
  'OMC Management',
  'Load OMC Stock Reports',
);

export const omcCreateStockReportActions = createApiActions<
  OmcStockReportReqInput,
  CreateOmcStockReportPayload
>('OMC Management', 'Create OMC Stock Report');

export const omcUpdateStockReportActions = createApiActions<
  OmcStockReportUpdateReqInput,
  UpdateOmcStockReportPayload
>('OMC Management', 'Update OMC Stock Report');

export const omcSubmitStockReportActions = createApiActions<string, SubmitOmcStockReportPayload>(
  'OMC Management',
  'Submit OMC Stock Report',
);

export const omcStockReportDetailActions = createApiActions<
  string,
  LoadOmcStockReportDetailResponse
>('OMC Management', 'Load OMC Stock Report Detail');

export const omcSubmitUsageReportActions = createApiActions<
  OmcProductUsageReqInput,
  SubmitOmcUsageReportPayload
>('OMC Management', 'Submit OMC Usage Report');

export const omcUpdateUsageReportActions = createApiActions<
  OmcUsageUpdateReqInput,
  UpdateOmcUsageReportPayload
>('OMC Management', 'Update OMC Usage Report');

export const omcCreateTerminalAgreementActions = createApiActions<
  OmcTerminalAgreementReqInput,
  CreateOmcTerminalAgreementPayload
>('OMC Management', 'Create OMC Terminal Agreement');

export const omcUpdateTerminalAgreementActions = createApiActions<
  OmcTerminalAgreementUpdateReqInput,
  UpdateOmcTerminalAgreementPayload
>('OMC Management', 'Update OMC Terminal Agreement');

export const omcSubmitTerminalAgreementActions = createApiActions<
  string,
  SubmitOmcTerminalAgreementPayload
>('OMC Management', 'Submit OMC Terminal Agreement');

export const omcTerminalSameTinActions = createApiActions<
  OmcTerminalSameTinReqInput,
  CheckOmcTerminalSameTinPayload
>('OMC Management', 'Check OMC Terminal Same TIN');

export const OmcSignContractActions = createApiActions<
  OmcContractSignReqInput,
  SignOmcContractResponsePayload
>('OMC Management', 'Sign OMC Contract');

export const omcCertificatePreviewActions = createApiActions<
  string,
  LoadOmcCertificatePreviewResponse
>('OMC Management', 'Load OMC Certificate Preview');

export const loggedInOmcLastMonthsProjectsActions = createApiActions<
  null,
  GetLoggedInOmcLastMonthDemandProjectionPayload
>('OMC Management', 'Get Logged In OMC Last Months Projects');

export const deleteTransitStockActions = createApiActions<
  { uuid: string },
  DeleteTransitStockPayload
>('OMC Management', 'Delete Transit Stock');

export const deleteTransitConsigneeActions = createApiActions<
  { uuid: string },
  DeleteTransitConsigneePayload
>('OMC Management', 'Delete Transit Consignee');

export const loadBpsCalendarActions = createApiActions<
  PageableParamInput,
  any
>('OMC Management', 'Load BPS Calendar');

export const deleteOmcDemandCargoProductActions = createApiActions<
  DeleteOmcDemandCargoProductInput,
  DeleteOmcDemandCargoProductPayload
>('OMC Management', 'Delete OMC Demand Cargo Product');

export const deleteOmcDemandProductUsageActions = createApiActions<
  DeleteOmcDemandProductUsageInput,
  DeleteOmcDemandProductUsagePayload
>('OMC Management', 'Delete OMC Demand Product Usage');



